import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, m } from "motion/react";
import {
  HiOutlineClipboard,
  HiCheck,
  HiArrowDown,
  HiOutlineCodeBracket,
  HiOutlineCpuChip,
  HiOutlineCloud,
  HiOutlineCommandLine,
} from "react-icons/hi2";
import { TiltSurface } from "./MotionPrimitives";

const CODE_LINES = [
  [{ t: "comment", v: "# inference.py" }],
  [],
  [
    { t: "kw", v: "from" },
    { t: "plain", v: " transformers " },
    { t: "kw", v: "import" },
    { t: "plain", v: " AutoModelForCausalLM, AutoTokenizer" },
  ],
  [
    { t: "kw", v: "import" },
    { t: "plain", v: " torch" },
  ],
  [],
  [
    { t: "fn", v: "model" },
    { t: "plain", v: " = AutoModelForCausalLM." },
    { t: "fn", v: "from_pretrained" },
    { t: "plain", v: "(" },
  ],
  [
    { t: "plain", v: "    " },
    { t: "str", v: '"meta-llama/Llama-3-8B"' },
    { t: "plain", v: "," },
  ],
  [
    { t: "plain", v: "    torch_dtype=torch." },
    { t: "fn", v: "bfloat16" },
    { t: "plain", v: "," },
  ],
  [
    { t: "plain", v: "    device_map=" },
    { t: "str", v: '"auto"' },
  ],
  [{ t: "plain", v: ")" }],
  [],
  [
    { t: "fn", v: "outputs" },
    { t: "plain", v: " = model." },
    { t: "fn", v: "generate" },
    { t: "plain", v: "(" },
  ],
  [
    { t: "plain", v: "    **inputs, max_new_tokens=" },
    { t: "num", v: "512" },
    { t: "plain", v: ", do_sample=" },
    { t: "kw", v: "True" },
  ],
  [{ t: "plain", v: ")" }],
];
const CODE = CODE_LINES.map((line) =>
  line.map((token) => token.v).join(""),
).join("\n");
const LAYERS = [
  {
    icon: HiOutlineCodeBracket,
    name: "Interface",
    tech: "JavaScript · React",
    color: "lime",
  },
  {
    icon: HiOutlineCommandLine,
    name: "API",
    tech: "Python · FastAPI",
    color: "blue",
  },
  {
    icon: HiOutlineCpuChip,
    name: "Intelligence",
    tech: "PyTorch · LLMs",
    color: "purple",
  },
  {
    icon: HiOutlineCloud,
    name: "Infrastructure",
    tech: "Azure · CI/CD",
    color: "lime",
  },
];

export default function CodeWorkspace({ still }) {
  const [tab, setTab] = useState("code");
  const [copyState, setCopyState] = useState("Copy code");
  const timer = useRef();
  const buttons = useRef([]);
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(CODE);
      setCopyState("Copied");
    } catch {
      setCopyState("Select code to copy");
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopyState("Copy code"), 2400);
  }

  function onTabKey(event, index) {
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? 1
          : ["ArrowLeft", "ArrowRight"].includes(event.key)
            ? 1 - index
            : null;
    if (next === null) return;
    event.preventDefault();
    setTab(next === 0 ? "code" : "stack");
    buttons.current[next]?.focus();
  }

  return (
    <TiltSurface still={still} className="home-editor">
      <div className="home-editor-chrome">
        <div className="home-window-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
        <span>sep / workspace</span>
        <HiOutlineCodeBracket aria-hidden="true" />
      </div>
      <div
        className="home-editor-tabs"
        role="tablist"
        aria-label="Workspace views"
      >
        {[
          { id: "code", title: "inference.py" },
          { id: "stack", title: "the stack" },
        ].map(({ id, title }, index) => (
          <button
            key={id}
            ref={(element) => {
              buttons.current[index] = element;
            }}
            id={`workspace-tab-${id}`}
            type="button"
            role="tab"
            aria-selected={tab === id}
            aria-controls={`workspace-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
            onKeyDown={(event) => onTabKey(event, index)}
            className={tab === id ? "is-active" : ""}
          >
            {id === "code" ? (
              <span className="home-python-mark" aria-hidden="true">
                py
              </span>
            ) : (
              <HiOutlineCpuChip />
            )}{" "}
            {title}
            {tab === id && (
              <m.span
                className="home-tab-indicator"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
          </button>
        ))}
      </div>
      <div className="home-editor-body">
        <AnimatePresence mode="wait" initial={false}>
          <m.div
            key={tab}
            id={`workspace-panel-${tab}`}
            role="tabpanel"
            aria-labelledby={`workspace-tab-${tab}`}
            tabIndex={0}
            initial={{ opacity: 0, y: still ? 0 : 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: still ? 0 : -8 }}
            transition={{ duration: 0.18 }}
          >
            {tab === "code" ? (
              <>
                <div className="home-code-toolbar">
                  <span>PYTHON / HUGGINGFACE</span>
                  <button
                    type="button"
                    onClick={copyCode}
                    aria-label={copyState}
                  >
                    {copyState === "Copied" ? (
                      <HiCheck />
                    ) : (
                      <HiOutlineClipboard />
                    )}
                    <span aria-live="polite">{copyState}</span>
                  </button>
                </div>
                <pre className="home-code">
                  <code>
                    {CODE_LINES.map((line, index) => (
                      <span className="home-code-line" key={index}>
                        <span className="home-line-number" aria-hidden="true">
                          {index + 1}
                        </span>
                        <span>
                          {line.map((token, tokenIndex) => (
                            <span
                              className={`home-token-${token.t}`}
                              key={tokenIndex}
                            >
                              {token.v}
                            </span>
                          ))}
                          {index < CODE_LINES.length - 1 ? "\n" : ""}
                        </span>
                      </span>
                    ))}
                  </code>
                </pre>
              </>
            ) : (
              <div className="home-architecture">
                <div className="home-code-toolbar">
                  <span>FROM FRONT END TO PRODUCTION</span>
                  <span>04 LAYERS</span>
                </div>
                {LAYERS.map(({ icon: Icon, name, tech, color }, index) => (
                  <React.Fragment key={name}>
                    <m.div
                      className={`home-layer home-layer-${color}`}
                      initial={{ opacity: 0, x: still ? 0 : 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.07 }}
                    >
                      <div className="home-layer-icon">
                        <Icon />
                      </div>
                      <div>
                        <strong>{name}</strong>
                        <span>{tech}</span>
                      </div>
                      <span className="home-layer-number">0{index + 1}</span>
                    </m.div>
                    {index < LAYERS.length - 1 && (
                      <div className="home-layer-connector" aria-hidden="true">
                        <HiArrowDown />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </m.div>
        </AnimatePresence>
      </div>
      <div className="home-editor-status">
        <span>
          <i /> {tab === "code" ? "inference.py" : "architecture"}
        </span>
        <span>
          {tab === "code" ? "Python · UTF-8" : "Interface → Infrastructure"}
        </span>
      </div>
    </TiltSurface>
  );
}
